import { Dispatch, SetStateAction, useRef, useState } from "react";
import {
  getTaskEditorComponentReorderIndex,
  isSameTaskEditorComponentDropPreview,
  parseTaskEditorComponentDragData,
  reorderDescriptionComponents,
  TaskEditorBlock,
  TaskEditorComponentDragData,
  TaskEditorComponentDropPreview,
} from "./taskEditorUtils";

// 컴포넌트 드래그는 description 배열 안에서만 동작.
// 첫 슬롯 (descriptionIndex === 0) 은 text 고정 — 드래그/드롭 차단.
function isDescriptionDropValid(
  description: { length: number },
  sourceDescriptionIndex: number,
  insertionIndex: number
): boolean {
  if (
    sourceDescriptionIndex < 0 ||
    sourceDescriptionIndex >= description.length
  )
    return false;
  if (insertionIndex <= 0) return false; // 첫 슬롯 보호
  if (insertionIndex > description.length) return false;
  return true;
}

type UseTaskEditorComponentDragArgs = {
  blocks: TaskEditorBlock[];
  setBlocks: Dispatch<SetStateAction<TaskEditorBlock[]>>;
  onBeforeReorder: () => void;
  cancelOtherDrag: () => void;
};

export type UseTaskEditorComponentDragReturn = {
  draggedComponent: TaskEditorComponentDragData | null;
  componentDropPreview: TaskEditorComponentDropPreview | null;
  handleComponentDragStart: (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    descriptionIndex: number
  ) => void;
  handleComponentDragOver: (
    event: React.DragEvent<HTMLElement>,
    nextPreview: TaskEditorComponentDropPreview
  ) => void;
  handleComponentDrop: (
    event: React.DragEvent<HTMLElement>,
    preview: TaskEditorComponentDropPreview
  ) => void;
  handleComponentDragEnd: () => void;
  handleComponentCardDragOver: (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    targetDescriptionIndex: number
  ) => void;
  handleComponentCardDrop: (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    targetDescriptionIndex: number
  ) => void;
  cancelDrag: () => void;
};

const DRAG_MIME = "application/task-editor-component";

export function useTaskEditorComponentDrag({
  blocks,
  setBlocks,
  onBeforeReorder,
  cancelOtherDrag,
}: UseTaskEditorComponentDragArgs): UseTaskEditorComponentDragReturn {
  const [draggedComponent, setDraggedComponent] =
    useState<TaskEditorComponentDragData | null>(null);
  const [componentDropPreview, setComponentDropPreview] =
    useState<TaskEditorComponentDropPreview | null>(null);
  const draggedRef = useRef<TaskEditorComponentDragData | null>(null);
  const dropPreviewRef = useRef<TaskEditorComponentDropPreview | null>(null);
  const reorderedDuringDragRef = useRef(false);

  const cancelDrag = () => {
    draggedRef.current = null;
    dropPreviewRef.current = null;
    reorderedDuringDragRef.current = false;
    setDraggedComponent(null);
    setComponentDropPreview(null);
  };

  const handleComponentDragStart = (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    descriptionIndex: number
  ) => {
    const block = blocks[blockIndex];
    if (!block) {
      event.preventDefault();
      return;
    }
    if (
      descriptionIndex <= 0 ||
      descriptionIndex >= block.description.length ||
      block.description.length <= 1
    ) {
      event.preventDefault();
      return;
    }
    cancelOtherDrag();
    const drag: TaskEditorComponentDragData = {
      blockIndex,
      descriptionIndex,
    };
    draggedRef.current = drag;
    reorderedDuringDragRef.current = false;
    setDraggedComponent(drag);
    dropPreviewRef.current = null;
    setComponentDropPreview(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(DRAG_MIME, JSON.stringify(drag));
  };

  const handleComponentDragOver = (
    event: React.DragEvent<HTMLElement>,
    nextPreview: TaskEditorComponentDropPreview
  ) => {
    const source =
      draggedRef.current ??
      draggedComponent ??
      parseTaskEditorComponentDragData(event.dataTransfer.getData(DRAG_MIME));
    if (!source || source.blockIndex !== nextPreview.blockIndex) return;
    const sourceBlock = blocks[source.blockIndex];
    if (!sourceBlock) return;
    if (
      !isDescriptionDropValid(
        sourceBlock.description,
        source.descriptionIndex,
        nextPreview.insertionIndex
      )
    ) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setComponentDropPreview((current) => {
      const resolved = isSameTaskEditorComponentDropPreview(
        current,
        nextPreview
      )
        ? current
        : nextPreview;
      dropPreviewRef.current = resolved;
      return resolved;
    });

    const previewIndex = getTaskEditorComponentReorderIndex(
      sourceBlock.description,
      source.descriptionIndex,
      nextPreview
    );
    if (previewIndex === null || previewIndex === source.descriptionIndex)
      return;

    if (!reorderedDuringDragRef.current) {
      onBeforeReorder();
      reorderedDuringDragRef.current = true;
    }

    setBlocks((currentBlocks) => {
      const block = currentBlocks[source.blockIndex];
      if (!block) return currentBlocks;
      const currentSource =
        draggedRef.current?.descriptionIndex ?? source.descriptionIndex;
      const currentTarget = getTaskEditorComponentReorderIndex(
        block.description,
        currentSource,
        nextPreview
      );
      if (
        currentTarget === null ||
        currentTarget === currentSource ||
        currentSource < 0 ||
        currentSource >= block.description.length
      ) {
        return currentBlocks;
      }
      if (currentTarget === 0) return currentBlocks; // 첫 슬롯 보호
      const nextDescription = reorderDescriptionComponents(
        block.description,
        currentSource,
        currentTarget
      );
      const out = [...currentBlocks];
      out[source.blockIndex] = { ...block, description: nextDescription };
      draggedRef.current = {
        blockIndex: source.blockIndex,
        descriptionIndex: currentTarget,
      };
      setDraggedComponent({
        blockIndex: source.blockIndex,
        descriptionIndex: currentTarget,
      });
      return out;
    });
  };

  const getCardDropPreview = (
    blockIndex: number,
    targetDescriptionIndex: number,
    event: React.DragEvent<HTMLElement>
  ): TaskEditorComponentDropPreview | null => {
    const source = draggedRef.current ?? draggedComponent;
    if (!source || source.blockIndex !== blockIndex) return null;
    if (targetDescriptionIndex === source.descriptionIndex) return null;
    const block = blocks[blockIndex];
    if (!block) return null;
    if (
      targetDescriptionIndex < 0 ||
      targetDescriptionIndex >= block.description.length
    )
      return null;
    const rect = event.currentTarget.getBoundingClientRect();
    const pointerY = event.clientY > 0 ? event.clientY : rect.bottom;
    const isBottomHalf = pointerY >= rect.top + rect.height / 2;
    return {
      blockIndex,
      insertionIndex: targetDescriptionIndex + (isBottomHalf ? 1 : 0),
    };
  };

  const handleComponentCardDragOver = (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    targetDescriptionIndex: number
  ) => {
    const preview = getCardDropPreview(
      blockIndex,
      targetDescriptionIndex,
      event
    );
    if (preview) handleComponentDragOver(event, preview);
  };

  const handleComponentCardDrop = (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    targetDescriptionIndex: number
  ) => {
    const preview = getCardDropPreview(
      blockIndex,
      targetDescriptionIndex,
      event
    );
    if (preview) handleComponentDrop(event, preview);
  };

  const handleComponentDrop = (
    event: React.DragEvent<HTMLElement>,
    _preview: TaskEditorComponentDropPreview
  ) => {
    event.preventDefault();
    cancelDrag();
  };

  const handleComponentDragEnd = () => cancelDrag();

  return {
    draggedComponent,
    componentDropPreview,
    handleComponentDragStart,
    handleComponentDragOver,
    handleComponentDrop,
    handleComponentDragEnd,
    handleComponentCardDragOver,
    handleComponentCardDrop,
    cancelDrag,
  };
}
